import { useCallback, useEffect, useRef, useState } from 'react';
interface UseTimerQueueOptions {
  duration?: number;
}
export function useTimerQueue<T>(options: UseTimerQueueOptions = {}) {
  const {
    duration = 10000
  } = options;
  const queueRef = useRef<T[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const [current, setCurrent] = useState<T | null>(null);
  const dequeue = useCallback(() => {
    return queueRef.current.shift() || null;
  }, []);
  const playNext = useCallback(() => {
    if (!isMountedRef.current) return;
    const nextItem = dequeue();
    if (nextItem) {
      setCurrent(nextItem);
      timerRef.current = setTimeout(() => {
        playNext();
      }, duration);
    } else {
      setCurrent(null);
      timerRef.current = null;
    }
  }, [dequeue, duration]);
  const skip = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    playNext();
  }, [playNext]);
  const enqueue = useCallback((item: T) => {
    queueRef.current.push(item);
    if (!timerRef.current) {
      playNext();
    }
  }, [playNext]);
  const clear = useCallback(() => {
    queueRef.current = [];
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setCurrent(null);
  }, []);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  return {
    current,
    enqueue,
    dequeue,
    skip,
    clear
  };
}